import React, { useEffect, useState, useRef } from 'react';
import { managementDb } from '../firebase';
import { ref, onValue } from 'firebase/database';
import { initializeAboutPageData } from '../firebase';
import { set, ref as dbRef } from 'firebase/database';

const AboutManagement = () => {
  const [team, setTeam] = useState([]);
  const [editingMember, setEditingMember] = useState(null);
  const [newMember, setNewMember] = useState({
    name: '',
    position: '',
    bio: '',
    skills: '',
    color: '',
    accent: '',
    image: '',
  });
  const [initLoading, setInitLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const addMemberBtnRef = useRef(null);
  const addModalRef = useRef(null);

  // Focus trap for modal
  useEffect(() => {
    if (showAddModal && addModalRef.current) {
      const focusableEls = addModalRef.current.querySelectorAll('input, textarea, button');
      if (focusableEls.length) focusableEls[0].focus();
      const handleTab = (e) => {
        const els = Array.from(focusableEls);
        const first = els[0];
        const last = els[els.length - 1];
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            if (document.activeElement === first) {
              e.preventDefault();
              last.focus();
            }
          } else {
            if (document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        }
        if (e.key === 'Escape') {
          setShowAddModal(false);
        }
      };
      const modalNode = addModalRef.current;
      modalNode.addEventListener('keydown', handleTab);
      return () => {
        if (modalNode) modalNode.removeEventListener('keydown', handleTab);
      };
    }
  }, [showAddModal]);

  // Restore focus to Add button when modal closes
  useEffect(() => {
    if (!showAddModal && addMemberBtnRef.current) {
      addMemberBtnRef.current.focus();
    }
  }, [showAddModal]);

  useEffect(() => {
    // Fetch team members
    const teamRef = ref(managementDb, 'about/team');
    const companyRef = ref(managementDb, 'about/company');

    const unsubTeam = onValue(teamRef, (snapshot) => {
      const data = snapshot.val();
      setTeam(Array.isArray(data) ? data : []);
    });
    const unsubCompany = onValue(companyRef, (snapshot) => {
      // setCompany(snapshot.val()); // Removed as per edit hint
      // setCompanyForm(c => snapshot.val() || c); // Removed as per edit hint
    });
    return () => {
      unsubTeam();
      unsubCompany();
    };
  }, []);

  // When team changes, select the first member by default if none selected
  useEffect(() => {
    if (team.length > 0 && !selectedMember) {
      setSelectedMember(team[0]);
    }
  }, [team, selectedMember]);

  // --- CRUD Functions ---
  const handleInitAboutData = async () => {
    setInitLoading(true);
    await initializeAboutPageData();
    setInitLoading(false);
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    const id = Date.now();
    const member = { ...newMember, id, skills: newMember.skills.split(',').map(s => s.trim()) };
    const updatedTeam = [...team, member];
    await set(dbRef(managementDb, 'about/team'), updatedTeam);
    setNewMember({ name: '', position: '', bio: '', skills: '', color: '', accent: '', image: '' });
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    const updatedTeam = team.map(m => m.id === editingMember.id ? { ...editingMember, skills: editingMember.skills.split(',').map(s => s.trim()) } : m);
    await set(dbRef(managementDb, 'about/team'), updatedTeam);
    setEditingMember(null);
  };

  const handleDeleteMember = async (id) => {
    const updatedTeam = team.filter(m => m.id !== id);
    await set(dbRef(managementDb, 'about/team'), updatedTeam);
  };

  // --- Render ---
  return (
    <div className="about-management-container">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-info-circle me-2"></i>
                About Management
              </h3>
              <button ref={addMemberBtnRef} className="btn btn-primary" onClick={() => setShowAddModal(true)}>
                <i className="fas fa-plus me-2"></i>
                Add New Member
              </button>
            </div>
            <div className="card-body">
              {/* Top Section with Margins */}
              <div className="top-section mb-4">
                {/* Initial Data Push Button */}
                {team.length === 0 && (
                  <button className="btn btn-primary" onClick={handleInitAboutData} disabled={initLoading}>
                    {initLoading ? 'Initializing...' : 'Initialize About Page Data'}
                  </button>
                )}
              </div>

              {/* Content Section (no About Page Management title/desc, no empty messages) */}
              <div className="content-section">
                {/* Two-column layout for team members and details using rows and cols */}
                <div className="row">
                  {/* Left: Member List */}
                  <div className="col-md-5 col-lg-4 border-end" style={{minHeight: 400}}>
                    <h6>Team Members</h6>
                    <div className="list-group">
                      {team.map(member => (
                        <button
                          key={member.id}
                          className={`list-group-item list-group-item-action d-flex align-items-center${selectedMember && selectedMember.id === member.id ? ' active' : ''}`}
                          onClick={() => setSelectedMember(member)}
                          style={{cursor: 'pointer'}}
                        >
                          <img src={member.image} alt={member.name} className="rounded-circle me-2" style={{width: 40, height: 40, objectFit: 'cover'}} />
                          <span>{member.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Right: Member Details or Add/Edit Form */}
                  <div className="col-md-7 col-lg-8">
                    {/* Add/Edit Form */}
                    {(!selectedMember || editingMember) ? (
                      <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h5 className="mb-0">{editingMember ? 'Edit Team Member' : 'Add New Team Member'}</h5>
                          {(editingMember || !selectedMember) && (
                            <button className="btn-close" onClick={() => { setEditingMember(null); setSelectedMember(team[0] || null); }} aria-label="Close"></button>
                          )}
                        </div>
                        <div className="card-body">
                          <form onSubmit={editingMember ? handleUpdateMember : handleAddMember} className="about-form">
                            <input className="form-control mb-1" placeholder="Name" value={editingMember ? editingMember.name : newMember.name} onChange={e => editingMember ? setEditingMember({ ...editingMember, name: e.target.value }) : setNewMember({ ...newMember, name: e.target.value })} required />
                            <input className="form-control mb-1" placeholder="Position" value={editingMember ? editingMember.position : newMember.position} onChange={e => editingMember ? setEditingMember({ ...editingMember, position: e.target.value }) : setNewMember({ ...newMember, position: e.target.value })} required />
                            <input className="form-control mb-1" placeholder="Image URL" value={editingMember ? editingMember.image : newMember.image} onChange={e => editingMember ? setEditingMember({ ...editingMember, image: e.target.value }) : setNewMember({ ...newMember, image: e.target.value })} required />
                            <input className="form-control mb-1" placeholder="Skills (comma separated)" value={editingMember ? editingMember.skills : newMember.skills} onChange={e => editingMember ? setEditingMember({ ...editingMember, skills: e.target.value }) : setNewMember({ ...newMember, skills: e.target.value })} />
                            <input className="form-control mb-1" placeholder="Color" value={editingMember ? editingMember.color : newMember.color} onChange={e => editingMember ? setEditingMember({ ...editingMember, color: e.target.value }) : setNewMember({ ...newMember, color: e.target.value })} />
                            <input className="form-control mb-1" placeholder="Accent" value={editingMember ? editingMember.accent : newMember.accent} onChange={e => editingMember ? setEditingMember({ ...editingMember, accent: e.target.value }) : setNewMember({ ...newMember, accent: e.target.value })} />
                            <textarea className="form-control mb-1" placeholder="Bio" value={editingMember ? editingMember.bio : newMember.bio} onChange={e => editingMember ? setEditingMember({ ...editingMember, bio: e.target.value }) : setNewMember({ ...newMember, bio: e.target.value })} />
                            <div className="d-flex gap-2 mt-2">
                              <button className="btn btn-primary btn-sm" type="submit">{editingMember ? 'Save' : 'Add Member'}</button>
                              {(editingMember || !selectedMember) && (
                                <button className="btn btn-secondary btn-sm" type="button" onClick={() => { setEditingMember(null); setSelectedMember(team[0] || null); }}>Cancel</button>
                              )}
                              {editingMember && (
                                <button className="btn btn-danger btn-sm ms-auto" type="button" onClick={() => { handleDeleteMember(editingMember.id); setEditingMember(null); setSelectedMember(team[0] || null); }}>Delete</button>
                              )}
                            </div>
                          </form>
                        </div>
                      </div>
                    ) : (
                      // Member Details
                      <div className="card">
                        <div className="card-body text-center">
                          <img src={selectedMember.image} alt={selectedMember.name} className="team-avatar mb-3" style={{width: 100, height: 100, objectFit: 'cover', borderRadius: '50%'}} />
                          <h5>{selectedMember.name}</h5>
                          <div className="position mb-2">{selectedMember.position}</div>
                          <div className="skills mb-2"><strong>Skills:</strong> {Array.isArray(selectedMember.skills) ? selectedMember.skills.join(', ') : selectedMember.skills}</div>
                          <div className="color-accent mb-2"><strong>Color:</strong> {selectedMember.color} <strong>Accent:</strong> {selectedMember.accent}</div>
                          <div className="bio mb-3">{selectedMember.bio}</div>
                          <div className="d-flex justify-content-center gap-2">
                            <button className="btn btn-outline-primary btn-sm" onClick={() => setEditingMember({ ...selectedMember, skills: Array.isArray(selectedMember.skills) ? selectedMember.skills.join(', ') : selectedMember.skills })}><i className="fas fa-edit me-1"></i>Edit</button>
                            <button className="btn btn-outline-danger btn-sm" onClick={() => { handleDeleteMember(selectedMember.id); setSelectedMember(team[0] || null); }}><i className="fas fa-trash me-1"></i>Delete</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="custom-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
          <div className="custom-modal-dialog" ref={addModalRef} tabIndex={-1}>
            <div className="custom-modal-header">
              <h5 className="mb-0">Add New Team Member</h5>
              <button className="btn-close" onClick={() => setShowAddModal(false)} aria-label="Close"></button>
            </div>
            <div className="custom-modal-body">
              <form onSubmit={e => { handleAddMember(e); setShowAddModal(false); }} className="about-form">
                <input className="form-control mb-1" placeholder="Name" value={newMember.name} onChange={e => setNewMember({ ...newMember, name: e.target.value })} required />
                <input className="form-control mb-1" placeholder="Position" value={newMember.position} onChange={e => setNewMember({ ...newMember, position: e.target.value })} required />
                <input className="form-control mb-1" placeholder="Image URL" value={newMember.image} onChange={e => setNewMember({ ...newMember, image: e.target.value })} required />
                <input className="form-control mb-1" placeholder="Skills (comma separated)" value={newMember.skills} onChange={e => setNewMember({ ...newMember, skills: e.target.value })} />
                <input className="form-control mb-1" placeholder="Color" value={newMember.color} onChange={e => setNewMember({ ...newMember, color: e.target.value })} />
                <input className="form-control mb-1" placeholder="Accent" value={newMember.accent} onChange={e => setNewMember({ ...newMember, accent: e.target.value })} />
                <textarea className="form-control mb-1" placeholder="Bio" value={newMember.bio} onChange={e => setNewMember({ ...newMember, bio: e.target.value })} />
                <div className="d-flex gap-2 mt-2">
                  <button className="btn btn-primary btn-sm" type="submit">Add Member</button>
                  <button className="btn btn-secondary btn-sm" type="button" onClick={() => setShowAddModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutManagement;